import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatCheck2Frontend } from "../actions/Checks/util";
import { Check, LAYER_TYPE, VIEW } from "../actions/Layers/constants";
import { callAvosApi } from "../utils/useAvosApiHook";

export interface LayerCheckParams {
    view_strategy?: string;
    check_location?: string;
    with_meta?: boolean;
    with_fruit?: boolean;
    children_level?: number;
    [key: string]: any;
}

export const getLayerChecksQueryParams = (layer: any, layerConfig: any): LayerCheckParams => {
    let view_strategy: string = VIEW.SELF;

    if (layerConfig?.show_children) {
        view_strategy = VIEW.CHILDREN;
    } else if (layerConfig?.show_checks?.view) {
        view_strategy = layerConfig.show_checks.view === VIEW.CHILDREN ? VIEW.CHILDREN : VIEW.SELF;
    } else if (layer?.type === LAYER_TYPE.PURCHASE_ORDER) {
        view_strategy = VIEW.CHILDREN;
    }

    return {
        view_strategy,
        with_meta: true,
        children_level: 2,
    };
};

/**
 * Hook to fetch checks for a specific layer.
 * Uses TanStack Query for automatic deduplication and caching.
 * Query key includes layer_id and all params to ensure proper cache isolation.
 *
 * @param layerId - The ID of the layer to fetch checks for
 * @param params - Query parameters (view_strategy, with_meta, children_level, etc.)
 * @returns { data: Check[], isLoading: boolean, isError: boolean, error: Error | null }
 *
 * @example
 * const { data: checks, isLoading } = useLayerChecks(layer?.id, {
 *   view_strategy: "self",
 *   with_meta: true,
 *   children_level: 2
 * });
 */
export const useLayerChecks = (tenantId: string | undefined, layerId: string | number | undefined, params?: LayerCheckParams) => {
    return useQuery({
        queryKey: ["layerChecks", tenantId, layerId, JSON.stringify(params || {})],
        queryFn: async (): Promise<Check[]> => {
            const { data } = await callAvosApi(`/${tenantId}/layers/${layerId}/checks`, {
                params,
            });
            // Apply the same formatting that was done in the Redux reducer
            return (data || []).map(formatCheck2Frontend);
        },
        // Support UUID/string ids and numeric ids; only block null/undefined/empty-string.
        enabled: !!tenantId && layerId !== undefined && layerId !== null && String(layerId).trim().length > 0,
    });
};

export const useConfiguredLayerChecks = (tenantId: string | undefined, layer: any, layerConfig: any) => {
    const params = getLayerChecksQueryParams(layer, layerConfig);
    const query = useLayerChecks(tenantId, layer?.id, params);

    return {
        ...query,
        params,
        view_strategy: params.view_strategy,
    };
};

/**
 * Hook to get the query client for manual cache invalidation.
 * Use when you need to invalidate checks cache after mutations.
 *
 * @example
 * const queryClient = useQueryClient();
 * await createCheck();
 * queryClient.invalidateQueries({ queryKey: ["layerChecks", layerId] });
 */
export const useLayerChecksInvalidation = (tenantId: string | undefined, layerId: string | number | undefined) => {
    const queryClient = useQueryClient();

    return {
        invalidateLayerChecks: () => queryClient.invalidateQueries({ queryKey: ["layerChecks", tenantId, layerId] }),
        invalidateAllLayerChecks: () => queryClient.invalidateQueries({ queryKey: ["layerChecks"] }),
    };
};
